import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n";
import { AppLayout } from "@/components/AppLayout";
import { DocPicker } from "@/components/DocPicker";
import { downloadReport } from "@/lib/report";
import { Button } from "@/components/ui/button";
import { CreditCard, XCircle, Car, Flame, Pause, User, ClipboardList, Map, Heart, Plane,
  Wand2, Loader2, Sparkles, Download, ArrowLeft } from "lucide-react";
import { AIText } from "@/components/AIText";

const ICONS = { "credit-card": CreditCard, "x-circle": XCircle, car: Car, flame: Flame, pause: Pause, user: User, clipboard: ClipboardList, map: Map, heart: Heart, plane: Plane };

export default function Simulator() {
  const { t, lang } = useI18n();
  const [scenarios, setScenarios] = useState([]);
  const [docId, setDocId] = useState("");
  const [active, setActive] = useState(null);
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/simulator/scenarios").then((r) => setScenarios(r.data)); }, []);

  const run = async (s) => {
    setActive(s); setAnswer(""); setLoading(true);
    try {
      const r = await api.post("/simulator", { scenario: s.key, document_id: docId || null, language: lang });
      setAnswer(r.data.answer);
    } catch { toast.error("Could not run simulation"); }
    finally { setLoading(false); }
  };

  const exportPdf = () => downloadReport({
    title: `What Happens If — ${active.label}`, language: lang,
    subtitle: "Scenario simulation", sections: [{ heading: active.label, body: answer }],
  });

  return (
    <AppLayout>
      <h1 className="font-display text-3xl font-semibold text-navy flex items-center gap-2"><Wand2 className="h-7 w-7 text-brandblue" /> {t("simulator_title")}</h1>
      <p className="mt-1 text-slate-500">{t("simulator_sub")}</p>

      <div className="mt-5 max-w-md">
        <label className="text-sm font-medium text-navy">{t("use_specific_policy")}</label>
        <div className="mt-1.5"><DocPicker testId="sim-doc" value={docId} onChange={setDocId} placeholder={t("sim_general")} /></div>
      </div>

      {!active ? (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {scenarios.map((s) => {
            const Icon = ICONS[s.icon] || Wand2;
            return (
              <button key={s.key} data-testid={`sim-${s.key}`} onClick={() => run(s)}
                className="flex flex-col items-center gap-3 rounded-2xl bg-white border border-slate-100 card-shadow p-5 hover:-translate-y-1 transition-transform">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl brand-gradient text-white"><Icon className="h-5 w-5" /></span>
                <span className="text-sm font-medium text-navy text-center leading-tight">{s.label}</span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="mt-6">
          <button onClick={() => { setActive(null); setAnswer(""); }} className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-navy"><ArrowLeft className="h-4 w-4" /> {t("all_scenarios")}</button>
          <div className="mt-3 rounded-3xl bg-white border border-slate-100 card-shadow p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-navy">{t("what_happens_if")} {active.label.toLowerCase()}?</h2>
              {answer && <Button data-testid="sim-export" variant="outline" onClick={exportPdf} className="rounded-full"><Download className="h-4 w-4 mr-2" />{t("export_pdf")}</Button>}
            </div>
            {loading ? (
              <div className="mt-6 flex items-center gap-2 text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /> {t("thinking_scenario")}</div>
            ) : (
              <div className="mt-3"><AIText>{answer}</AIText></div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
}
